
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { studentService } from '../../services/studentService';
import { programService } from '../../services/programService';
import { hierarchyService } from '../../services/hierarchyService';
import { supabase } from '../../lib/supabase';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
// Assuming AppIcon wrapper exists, otherwise use lucide-react directly if installed
import AppIcon from '../../components/AppIcon';
import AdminLayout from '../../components/layouts/AdminLayout'; // Import Layout

const StudentList = ({ embedded = false, onViewStudent }) => {
    const navigate = useNavigate();

    const calculateStage = (moduleNum) => {
        const mod = Number(moduleNum) || 1;
        const year = Math.ceil(mod / 2);
        const sem = mod % 2 === 0 ? 2 : 1;
        return `Y${year}M${sem}`;
    };
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const studentsLoadedRef = useRef(false);
    const studentsRequestRef = useRef(0);
    const filtersMountedRef = useRef(false);
    const [filters, setFilters] = useState({
        search: '',
        departmentId: '',
        programId: '',
        academicLevel: '',
        intake: '',
        status: ''
    });

    const [departments, setDepartments] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [intakes, setIntakes] = useState([]);

    useEffect(() => {
        fetchDepartments();
        fetchPrograms();
        fetchIntakes();
    }, []);

    // Search is intentionally local: the scoped student list is already in
    // memory, so typing never starts a request or replaces the table loader.
    // Only the non-search filters need to refresh the scoped list.
    useEffect(() => {
        const delay = filtersMountedRef.current ? 120 : 0;
        filtersMountedRef.current = true;
        const timer = setTimeout(() => fetchStudents({
            departmentId: filters.departmentId,
            programId: filters.programId,
            academicLevel: filters.academicLevel,
            intake: filters.intake,
            status: filters.status,
        }), delay);
        return () => clearTimeout(timer);
    }, [filters.departmentId, filters.programId, filters.academicLevel, filters.intake, filters.status]);

    const fetchDepartments = async () => {
        try {
            const data = await hierarchyService.getDepartments();
            setDepartments(data || []);
        } catch (error) {
            console.error("Failed to load departments", error);
        }
    };

    const fetchPrograms = async () => {
        try {
            const data = await programService.getPrograms();
            setPrograms(data || []);
        } catch (error) {
            console.error("Failed to load programs", error);
        }
    };

    const fetchIntakes = async () => {
        try {
            // Fetch unique intake codes from students
            const { data, error } = await supabase
                .from('students')
                .select('intake_code')
                .not('intake_code', 'is', null)
                .order('intake_code', { ascending: false });

            if (!error && data) {
                const uniqueIntakes = [...new Set(data.map(s => s.intake_code))];
                setIntakes(uniqueIntakes);
            }
        } catch (error) {
            console.error("Failed to load intakes", error);
        }
    };

    const fetchStudents = async (queryFilters = filters) => {
        const requestId = ++studentsRequestRef.current;
        const showInitialLoader = !studentsLoadedRef.current && students.length === 0;
        if (showInitialLoader) setLoading(true);
        try {
            const data = await studentService.getStudents(queryFilters);
            // A fast sequence such as "i" → "il" → "ilo" can resolve out of
            // order. Only the latest query is allowed to update the table.
            if (requestId !== studentsRequestRef.current) return;
            setStudents(data || []);
            studentsLoadedRef.current = true;
        } catch (error) {
            console.error("Failed to load students", error);
        } finally {
            if (requestId === studentsRequestRef.current) setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const visibleStudents = useMemo(() => {
        const term = filters.search.trim().toLowerCase();
        if (!term) return students;
        return students.filter(student => [
            student.admission_number,
            student.email,
            student.institutional_email,
            student.personal_email,
            student.first_name,
            student.last_name,
            student.full_name,
            student.program?.name,
            student.program?.code,
        ].some(value => String(value || '').toLowerCase().includes(term)));
    }, [filters.search, students]);

    const Layout = embedded ? React.Fragment : ({ children }) => <AdminLayout title="Student Management">{children}</AdminLayout>;
    return (
        <Layout>
            <div className="space-y-6">
                {/* Single-line Minimalistic Toolbar */}
                <div className="flex flex-wrap xl:flex-nowrap items-center justify-between gap-2.5 rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <h1 className="text-lg font-bold tracking-tight text-slate-900">Student Management</h1>
                        <span className="text-xs text-slate-500 font-mono">· {visibleStudents.length} total</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 flex-1 justify-end">
                        <div className="w-full min-w-0 sm:w-48">
                            <Input
                                placeholder="Search name / admission..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                startIcon="search"
                                className="h-8 text-xs"
                            />
                        </div>

                        <Select
                            placeholder="All Departments"
                            options={[
                                { value: '', label: 'All Departments' },
                                ...departments.map(d => ({ value: d.id, label: d.name }))
                            ]}
                            value={filters.departmentId}
                            onChange={(value) => {
                                handleFilterChange('departmentId', value);
                                handleFilterChange('programId', '');
                                handleFilterChange('academicLevel', '');
                            }}
                            className="h-8 w-full text-xs sm:w-auto sm:max-w-[130px]"
                        />

                        <Select
                            placeholder="All Programs"
                            options={[
                                { value: '', label: 'All Programs' },
                                ...programs
                                    .filter(p => !filters.departmentId || p.department_id === filters.departmentId)
                                    .map(p => ({ value: p.id, label: `${p.code} - ${p.name}` }))
                            ]}
                            value={filters.programId}
                            onChange={(value) => {
                                handleFilterChange('programId', value);
                                handleFilterChange('academicLevel', '');
                            }}
                            className="h-8 w-full text-xs sm:w-auto sm:max-w-[130px]"
                        />

                        <Select
                            placeholder="All Levels"
                            disabled={!filters.programId}
                            options={[
                                { value: '', label: 'All Levels' },
                                ...(
                                    (() => {
                                        const selectedProgram = programs.find(p => p.id === filters.programId);
                                        if (selectedProgram && selectedProgram.levels && selectedProgram.levels.length > 0) {
                                            const dbLevelMapping = {
                                                'level_3': 'Level 3',
                                                'level_4_artisan': 'Level 4',
                                                'level_5_craft': 'Level 5',
                                                'level_6_diploma': 'Level 6'
                                            };
                                            return selectedProgram.levels.map(lvl => ({
                                                value: dbLevelMapping[lvl] || lvl,
                                                label: dbLevelMapping[lvl] || lvl
                                            }));
                                        }
                                        return [];
                                    })()
                                )
                            ]}
                            value={filters.academicLevel}
                            onChange={(value) => handleFilterChange('academicLevel', value)}
                            className="h-8 w-full text-xs sm:w-auto sm:max-w-[110px]"
                        />

                        <Select
                            placeholder="All Intakes"
                            options={[
                                { value: '', label: 'All Intakes' },
                                ...intakes.map(intake => ({ value: intake, label: intake }))
                            ]}
                            value={filters.intake}
                            onChange={(value) => handleFilterChange('intake', value)}
                            className="h-8 w-full text-xs sm:w-auto sm:max-w-[110px]"
                        />

                        <Select
                            placeholder="Status"
                            options={[
                                { value: '', label: 'All Statuses' },
                                { value: 'Pending', label: 'Pending' },
                                { value: 'Admitted', label: 'Admitted' },
                                { value: 'Active', label: 'Active' },
                                { value: 'Inactive', label: 'Inactive' },
                                { value: 'Suspended', label: 'Suspended' },
                                { value: 'Withdrawn', label: 'Withdrawn' },
                                { value: 'Graduated', label: 'Graduated' }
                            ]}
                            value={filters.status}
                            onChange={(value) => handleFilterChange('status', value)}
                            className="h-8 w-full text-xs sm:w-auto sm:max-w-[110px]"
                        />

                        <Button size="sm" className="h-8 text-xs font-semibold shrink-0" onClick={() => navigate('/students/new')}>
                            <AppIcon name="plus" className="mr-1.5 h-3.5 w-3.5" />
                            Add New Student
                        </Button>
                    </div>
                </div>

                {/* Minimalistic Serialized Table */}
                <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500 border-b border-slate-200/80">
                                <tr>
                                    <th className="w-12 px-4 py-3">#</th>
                                    <th className="px-4 py-3">Admission No</th>
                                    <th className="px-4 py-3">Student Name</th>
                                    <th className="px-4 py-3">Program</th>
                                    <th className="px-4 py-3">Level</th>
                                    <th className="px-4 py-3">Intake</th>
                                    <th className="px-4 py-3">Stage</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan="9" className="px-6 py-10 text-center text-slate-400">
                                            Loading students...
                                        </td>
                                    </tr>
                                ) : visibleStudents.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="px-6 py-10 text-center text-slate-400">
                                            No students found matching current filters.
                                        </td>
                                    </tr>
                                ) : (
                                    visibleStudents.map((student, index) => (
                                        <tr key={student.profile_id || index} className="hover:bg-slate-50/70 transition-colors">
                                            <td className="px-4 py-3 font-semibold text-slate-400">
                                                {index + 1}
                                            </td>
                                            <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-900">
                                                {student.admission_number || 'N/A'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="h-7 w-7 rounded-full bg-slate-100 border border-slate-200/60 flex items-center justify-center text-slate-700 text-xs font-bold shrink-0">
                                                        {(student.first_name?.[0] || student.full_name?.[0] || '?').toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-slate-900">{student.first_name ? `${student.first_name} ${student.last_name || ''}` : student.full_name}</div>
                                                        <div className="text-xs text-slate-400 font-mono">{student.institutional_email || student.email || '—'}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-xs font-medium text-slate-700">
                                                {student.program?.code || 'N/A'}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-600">
                                                {student.academic_level || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-xs font-mono text-slate-600">
                                                {student.intake_code || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-xs">
                                                <span className="font-semibold text-slate-900">{calculateStage(Math.max(Number(student.current_module || 0), Number(student.starting_module || 0)))}</span>
                                                <div className="text-[10px] text-slate-400 uppercase font-mono">Mod {Math.max(Number(student.current_module || 0), Number(student.starting_module || 0)) || 1}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize border
                                                    ${student.status?.toLowerCase() === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                                        student.status?.toLowerCase() === 'admitted' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                                            student.status?.toLowerCase() === 'inactive' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                                                student.status?.toLowerCase() === 'pending' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                                                    ['suspended', 'withdrawn'].includes(student.status?.toLowerCase()) ? 'bg-rose-50 text-rose-700 border-rose-200' :
                                                                        'bg-slate-50 text-slate-700 border-slate-200'}`}>
                                                    {student.status || 'Pending'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <Button size="sm" variant="outline" className="h-7 text-xs px-2.5 font-medium" onClick={() => onViewStudent ? onViewStudent(student.profile_id) : navigate(`/students/${student.profile_id}`)}>
                                                    View
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default StudentList;
